import { useState } from 'react';
import { toast } from 'sonner';
import { DatePicker } from '@/components/ui/DatePicker';
import { formatDateOnly } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const PAST_YEARS = { past: 40, future: 10 };

interface EditableDateProps {
  /** `YYYY-MM-DD` or null. */
  value: string | null;
  onSave: (next: string | null) => Promise<void> | void;
  canEdit: boolean;
  ariaLabel: string;
  placeholder?: string;
  align?: 'left' | 'right';
  className?: string;
}

/**
 * Click-to-pick date for the equipment KV rows. Commits as soon as a
 * day (or Clear) is picked, the same way the other inline editors
 * commit on Enter.
 */
export function EditableDate({
  value,
  onSave,
  canEdit,
  ariaLabel,
  placeholder = '—',
  align = 'right',
  className,
}: EditableDateProps) {
  const [pending, setPending] = useState(false);

  if (!canEdit) {
    return (
      <span className={cn(!value && 'text-muted-foreground', className)}>
        {value ? formatDateOnly(value) : placeholder}
      </span>
    );
  }

  return (
    <DatePicker
      value={value ?? ''}
      aria-label={ariaLabel}
      placeholder={placeholder}
      disabled={pending}
      showShortcuts={false}
      yearRange={PAST_YEARS}
      onChange={async (next) => {
        const normalized = next || null;
        if (normalized === (value ?? null)) return;
        try {
          setPending(true);
          await onSave(normalized);
        } catch (err) {
          toast.error(err instanceof Error ? err.message : `Couldn't save ${ariaLabel}.`);
        } finally {
          setPending(false);
        }
      }}
      className={cn(
        'h-8 w-auto max-w-full border-transparent bg-transparent px-1.5 hover:border-border hover:bg-secondary/60',
        align === 'right' && 'ml-auto',
        className,
      )}
    />
  );
}
